import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Truck, ScanLine, PackageCheck, LogOut, Package, AlertCircle } from "lucide-react";

export default function RepartidorHome() {
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [batches, setBatches] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [clienteId, setClienteId] = useState("");
  const nav = useNavigate();

  useEffect(() => {
    api.get("/dashboard/repartidor").then((r) => setData(r.data));
    api.get("/batches").then((r) => setBatches(r.data));
  }, []);

  const openNew = async () => {
    setShowNew(true);
    if (clientes.length === 0) {
      // repartidor can't call /users; use a lightweight endpoint. For now let admin/rep both fetch:
      try {
        const r = await api.get("/users?role=cliente");
        setClientes(r.data.filter((c) => c.active));
      } catch { /* rep may not have permission */ }
    }
  };

  const createBatch = async (e) => {
    e.preventDefault();
    const r = await api.post("/batches", { cliente_id: clienteId, tipo: "retiro" });
    nav(`/repartidor/lote/${r.data.id}`);
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white bg-noise pb-24">
      {/* Mobile header */}
      <header className="border-b border-white/10 bg-zinc-950/90 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-md mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-yellow-300 flex items-center justify-center">
              <Truck className="w-5 h-5 text-black" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-display font-black text-sm leading-none">VOYCARGO</div>
              <div className="font-mono-tech text-[9px] text-yellow-300 uppercase tracking-widest">Repartidor</div>
            </div>
          </div>
          <button onClick={logout} className="w-9 h-9 border border-white/10 flex items-center justify-center" data-testid="logout-btn">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>

      <main className="max-w-md mx-auto px-4 py-6">
        <div className="mb-6">
          <div className="font-mono-tech text-xs text-zinc-500 uppercase tracking-widest">Hola,</div>
          <div className="font-display font-black text-3xl tracking-tighter">{user?.name}</div>
        </div>

        {data && (
          <div className="grid grid-cols-2 gap-3 mb-6">
            <div className="bg-zinc-900 border border-white/10 p-4">
              <div className="font-mono-tech text-[10px] uppercase tracking-widest text-zinc-500 mb-2">En reparto</div>
              <div className="font-display font-black text-3xl">{data.en_reparto}</div>
            </div>
            <div className="bg-zinc-900 border border-white/10 p-4">
              <div className="font-mono-tech text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Entregados hoy</div>
              <div className="font-display font-black text-3xl text-emerald-300">{data.entregados_hoy}</div>
            </div>
            <div className="bg-zinc-900 border border-white/10 p-4">
              <div className="font-mono-tech text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Incidencias hoy</div>
              <div className="font-display font-black text-3xl text-red-300">{data.no_entregados_hoy}</div>
            </div>
            <div className="bg-zinc-900 border border-white/10 p-4">
              <div className="font-mono-tech text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Lotes abiertos</div>
              <div className="font-display font-black text-3xl text-yellow-300">{data.lotes_abiertos}</div>
            </div>
          </div>
        )}

        {/* Big actions */}
        <div className="space-y-3 mb-8">
          <button onClick={openNew} data-testid="btn-nuevo-lote" className="w-full bg-yellow-300 text-black font-bold p-5 uppercase tracking-widest flex items-center justify-between hover:bg-yellow-200">
            <span className="flex items-center gap-3"><ScanLine className="w-6 h-6" /> Nuevo lote de retiro</span>
            <span className="font-mono-tech text-xs">→</span>
          </button>
          <Link to="/repartidor/ruta" data-testid="btn-ver-ruta" className="w-full border border-white/20 hover:border-yellow-300 text-white font-bold p-5 uppercase tracking-widest flex items-center justify-between">
            <span className="flex items-center gap-3"><PackageCheck className="w-6 h-6 text-yellow-300" /> Ruta del día</span>
            <span className="font-mono-tech text-xs text-yellow-300">{data?.en_reparto || 0}</span>
          </Link>
        </div>

        {showNew && (
          <form onSubmit={createBatch} className="mb-6 border border-yellow-300/40 bg-yellow-300/5 p-5">
            <div className="font-mono-tech text-xs text-yellow-300 uppercase tracking-widest mb-3">/ Selecciona el cliente</div>
            <select required value={clienteId} onChange={(e) => setClienteId(e.target.value)} className="w-full bg-zinc-900 border border-white/20 focus:border-yellow-300 outline-none px-3 py-3 text-sm font-mono-tech mb-3" data-testid="cliente-select">
              <option value="">Elige cliente / aliado</option>
              {clientes.map((c) => <option key={c.id} value={c.id}>{c.razon_social || c.name}</option>)}
            </select>
            <div className="flex gap-2">
              <button type="submit" className="flex-1 bg-yellow-300 text-black font-bold py-3 uppercase tracking-widest text-sm" data-testid="create-batch">Abrir lote</button>
              <button type="button" onClick={() => setShowNew(false)} className="border border-white/20 px-4 py-3 uppercase tracking-widest text-sm">Cancelar</button>
            </div>
          </form>
        )}

        {batches.length > 0 && (
          <div>
            <div className="font-mono-tech text-xs text-zinc-500 uppercase tracking-widest mb-3">/ Mis lotes</div>
            <div className="space-y-2">
              {batches.slice(0, 5).map((b) => (
                <Link key={b.id} to={`/repartidor/lote/${b.id}`} className="block border border-white/10 hover:border-yellow-300/40 p-4 bg-zinc-900" data-testid={`lote-${b.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-display font-bold">{b.cliente_name}</div>
                    <span className={`font-mono-tech text-[10px] px-2 py-0.5 border uppercase tracking-widest ${b.state === "abierto" ? "border-yellow-300/40 text-yellow-300" : "border-white/10 text-zinc-500"}`}>
                      {b.state}
                    </span>
                  </div>
                  <div className="font-mono-tech text-xs text-zinc-500">{b.order_ids?.length || 0} pedidos · {new Date(b.opened_at).toLocaleString("es-CL")}</div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
